// S16 MCP consent (architecture 7.4, REQ-11.4): "Allow Claude (claude.ai) to work on Feedback tickets
// for you?" with what it can do, Allow / Deny (CT-024: no AI platform; a client runs on its own AI). The decision answers with the client's
// callback URL, and the browser goes there (a full-page navigation). poc CT-013: one line says how long a
// connection lasts; the decision may instead send the person through Microsoft sign-in again (a relative URL).
// poc CT-015: for a published identity too, the name is the one the Admins registered (never its document's
// client_name), and "Returns to" is the redirect URI's origin.
import { MCP_CONNECTION_LIFETIME_TEXT } from '@feedback/shared/constants/mcp-connections';
import type { AuthorizationRequestView } from '@feedback/shared/contracts/mcp';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Bot } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api, isApiError, seg } from '@/lib/api';
import { formatTime } from '@/lib/format';

export const Route = createFileRoute('/oauth/consent')({
  // The key is always returned (undefined when missing or malformed), so a raw value can't survive.
  validateSearch: (search: Record<string, unknown>): { request?: string } => ({
    request: typeof search.request === 'string' && search.request ? search.request : undefined,
  }),
  component: Consent,
});

function Consent() {
  const { request: id } = Route.useSearch();
  const request = useQuery({
    queryKey: ['oauth-request', id],
    queryFn: () => api.get<AuthorizationRequestView>(`/api/oauth/requests/${seg(id ?? '')}`),
    enabled: Boolean(id),
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api.post<{ redirectUrl: string }>(`/api/oauth/requests/${seg(id ?? '')}/decision`, { approve }),
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  });
  const gone = !id || (isApiError(request.error) && request.error.status === 404);
  return (
    <Card className="mx-auto max-w-md">
      <CardContent className="flex flex-col gap-4 p-6">
        {gone ? (
          <EmptyState title="This request has expired">
            Go back to the app you were connecting and start again. Requests last 10 minutes and work once.
          </EmptyState>
        ) : request.isPending ? (
          <LoadingState rows={3} label="Loading the request" />
        ) : request.isError ? (
          <ErrorState error={request.error} onRetry={() => void request.refetch()} />
        ) : (
          <>
            <Bot className="size-8 text-primary" aria-hidden="true" />
            <h1 className="text-xl font-semibold">
              Allow <span className="whitespace-nowrap">{request.data.client.name}</span> to work on Feedback tickets
              for you?
            </h1>
            <p className="text-sm text-muted">Signed in as {request.data.person.displayName}.</p>
            <div className="flex flex-col gap-1 text-sm">
              <p>
                <span className="font-medium">It will be able to: </span>
                {request.data.capabilities}.
              </p>
              <ul className="list-disc pl-5">
                <li>Read tickets in the apps you work on</li>
                <li>Take and update tickets, comment on them, as far as your role allows</li>
                <li>
                  Act as you. Ticket history shows “{request.data.client.name} on behalf of{' '}
                  {request.data.person.displayName}”
                </li>
              </ul>
            </div>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted">Returns to</dt>
              <dd className="break-all">{request.data.redirectOrigin}</dd>
              <dt className="text-muted">Expires</dt>
              <dd>{formatTime(request.data.expiresAt)}</dd>
            </dl>
            <p className="text-sm" data-testid="consent-lifetime">
              {MCP_CONNECTION_LIFETIME_TEXT}
            </p>
            <p className="text-xs text-muted">You can disconnect it any time in Notifications, under Connected apps.</p>
            <div className="flex flex-wrap gap-2">
              <Button disabled={decide.isPending} onClick={() => decide.mutate(true)}>
                Allow
              </Button>
              <Button variant="secondary" disabled={decide.isPending} onClick={() => decide.mutate(false)}>
                Deny
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
